<script lang="ts" setup>
import {
  tpgIconClose,
  tpgIconImage,
  tpgIconOpenInNew,
} from '@tpg/grammar-web-icons';
import {
  computed,
  onMounted,
  ref,
  watch,
} from 'vue';

import {
  GrammarDivider,
  GrammarOverlayCard,
  GrammarQuaternaryButton,
} from '@nora-monorepo/grammar-ui';
import type { FileResultDTO } from '@nora-monorepo/nora-api-client';
import { truncateText } from '@nora-monorepo/ui-shared';

import FilePreview from '#nora-ui/components/files/FilePreview.vue';
import { useMapStore } from '#nora-ui/map/stores/map-store';
import { useFileStore } from '#nora-ui/stores/file-store';

interface Props {
  mapId: string;
}

const props = defineProps<Props>();

const mapStore = useMapStore();
const fileStore = useFileStore();

const map = mapStore.getMapByIdOrThrow(props.mapId);

const selectedFile = computed(() => {
  return map.state.file.selectedFile;
});

const file = ref<FileResultDTO>();

async function loadFile() {
  if (!selectedFile.value) {
    return;
  }

  file.value = await fileStore.loadFileDetails({
    domain: selectedFile.value.domain,
    domainId: selectedFile.value.domainId,
    id: selectedFile.value.id,
    path: selectedFile.value.path,
  });
}

async function openFileDetails() {
  if (!selectedFile.value) {
    return;
  }

  await fileStore.openFileDetails({
    domain: selectedFile.value.domain,
    domainId: selectedFile.value.domainId,
    id: selectedFile.value.id,
    path: selectedFile.value.path,
  });
}

function closeFileOverlay() {
  map.state.file.clearSelectedFile();
}

const truncatedFileName = computed(() => {
  if (!file.value?.fileName) {
    return '';
  }
  return truncateText(file.value.fileName, 20);
});

watch(selectedFile, async (newFile) => {
  if (newFile) {
    await loadFile();
  }
  else {
    file.value = undefined;
  }
});

onMounted(async () => {
  await loadFile();
});
</script>

<template>
  <div v-if="file">
    <GrammarOverlayCard
      class="w-[312px] override-min-width pointer-events-auto"
      :leading-icon-template="tpgIconImage"
      :heading-label="truncatedFileName"
    >
      <template #header-trailing-buttons>
        <div class="flex flex-row items-center justify-between">
          <GrammarDivider orientation="vertical" />
          <GrammarQuaternaryButton
            :icon-template="tpgIconOpenInNew"
            @click="openFileDetails"
          />
          <GrammarDivider orientation="vertical" />
          <GrammarQuaternaryButton
            :icon-template="tpgIconClose"
            @click="closeFileOverlay"
          />
        </div>
      </template>
      <div class="overflow-x-hidden py-2">
        <FilePreview
          :file="file"
          :width="312"
          :height="175"
        />
      </div>
    </GrammarOverlayCard>
  </div>
</template>
